// Who is signed in, which stores they have, and which store is current.
// Contracts: GET /api/auth/me -> Me (auth module); GET /api/admin/store -> StoreDetail (store module).
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, getStoreId, setStoreId } from './api'
import type { Permission } from '@shared/types'

export type { StoreSummary, Me, StoreDetail } from '@shared/api'
import type { Me, StoreDetail } from '@shared/api'

interface SessionCtx {
  me: Me | null
  loading: boolean
  storeId: string | null
  store: StoreDetail | null
  switchStore: (id: string) => void
  can: (p: Permission) => boolean
  refresh: () => void
}
const Ctx = createContext<SessionCtx | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const meQ = useQuery({ queryKey: ['me'], queryFn: () => api.get<Me>('/api/auth/me'), retry: false, staleTime: 60_000 })
  const [storeId, setSid] = useState<string | null>(getStoreId())
  const me = meQ.data ?? null

  // Keep the current store valid for this user.
  useEffect(() => {
    if (!me) return
    const valid = me.stores.some(s => s.id === storeId)
    if (!valid) { const next = me.stores[0]?.id ?? null; setStoreId(next); setSid(next) }
  }, [me, storeId])

  const storeQ = useQuery({
    queryKey: ['store', storeId], enabled: !!me && !!storeId,
    queryFn: () => api.get<StoreDetail>('/api/admin/store', { headers: { 'X-Store-Id': storeId ?? '' } }), staleTime: 30_000,
  })

  useEffect(() => {
    const onExpired = () => { qc.setQueryData(['me'], null); qc.invalidateQueries({ queryKey: ['me'] }) }
    window.addEventListener('auth:expired', onExpired)
    return () => window.removeEventListener('auth:expired', onExpired)
  }, [qc])

  const value = useMemo<SessionCtx>(() => ({
    me, loading: meQ.isLoading || (!!storeId && !!me && storeQ.isLoading),
    storeId, store: storeQ.data ?? null,
    switchStore: (id: string) => { setStoreId(id); setSid(id); qc.removeQueries({ predicate: q => q.queryKey[0] !== 'me' }) },
    can: (p: Permission) => { const s = storeQ.data; return !!s && (s.role !== 'staff' || s.permissions.includes(p)) },
    refresh: () => { qc.invalidateQueries({ queryKey: ['me'] }); qc.invalidateQueries({ queryKey: ['store'] }) },
  }), [me, meQ.isLoading, storeId, storeQ.data, storeQ.isLoading, qc])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useSession() {
  const v = useContext(Ctx)
  if (!v) throw new Error('useSession outside SessionProvider')
  return v
}
/** The current store; only call inside 'store'/'full' layouts (guaranteed loaded there). */
export function useStore(): StoreDetail {
  const s = useSession().store
  if (!s) throw new Error('useStore called before the store loaded')
  return s
}
